import { useMemo, useState, type FormEvent } from 'react';
import { loadStripe, type Stripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { Button } from '@components/v4/Button/Button';
import { V4 } from '@components/v4/tokens';

/* global STRIPE_PUBLISHABLE_KEY */

/**
 * Singleton de la promesa de Stripe — loadStripe debe llamarse una sola vez
 * por publishable key. Si la key no está configurada, queda null y la UI
 * muestra un fallback (la rama tarjeta queda detrás de la key, FR-28).
 */
let stripePromise: Promise<Stripe | null> | null = null;
export const getStripePromise = (): Promise<Stripe | null> | null => {
  if (!STRIPE_PUBLISHABLE_KEY) return null;
  if (!stripePromise) stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);
  return stripePromise;
};

export const isStripeConfigured = (): boolean => Boolean(STRIPE_PUBLISHABLE_KEY);

interface StripePaymentFormProps {
  /** client_secret devuelto por createSubscription/convertFreemium (metodo_pago=tarjeta). */
  clientSecret: string;
  /** Se invoca cuando el PaymentIntent queda succeeded o processing (incl. tras 3DS). */
  onSuccess: () => void;
  onCancel?: () => void;
}

// Formulario interno — debe vivir dentro de <Elements>. Usa PaymentElement
// (soporta tarjeta + 3DS/SCA automáticamente) y confirmPayment con redirect
// 'if_required' para resolver el desafío 3DS in-app cuando se puede.
const InnerForm = ({ onSuccess, onCancel }: Omit<StripePaymentFormProps, 'clientSecret'>) => {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  // Gate de 'ready': el PaymentElement emite onReady cuando terminó de montarse. Confirmar antes de
  // ese evento hace que Stripe LANCE un IntegrationError ("could not retrieve data from the
  // specified Element"). El botón queda deshabilitado hasta ready para que ese throw no ocurra.
  const [ready, setReady] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements || !ready) return;

    setSubmitting(true);
    setErrorMsg(null);

    try {
      // confirmPayment puede LANZAR (throw) ante un IntegrationError (p. ej. elemento no montado),
      // no solo devolver { error }. Sin este try/catch el throw dejaría el spinner colgado para
      // siempre y produciría un unhandled rejection.
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        // redirect: 'if_required' resuelve 3DS/SCA in-app cuando el banco lo permite;
        // solo redirige a return_url si el método de pago lo exige.
        redirect: 'if_required',
        confirmParams: {
          return_url: `${window.location.origin}/mi-cuenta`,
        },
      });

      if (error) {
        setErrorMsg(error.message || 'No se pudo procesar el pago. Verifica los datos de tu tarjeta.');
        setSubmitting(false);
        return;
      }

      if (paymentIntent && (paymentIntent.status === 'succeeded' || paymentIntent.status === 'processing')) {
        onSuccess();
        return;
      }

      setErrorMsg('El pago quedó pendiente de confirmación. Revisa el estado de tu suscripción en unos minutos.');
      setSubmitting(false);
    } catch {
      // IntegrationError u otro throw inesperado: NUNCA dejar el spinner colgado.
      setErrorMsg('No pudimos procesar el pago. Revisa tu tarjeta e intenta de nuevo.');
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <PaymentElement options={{ layout: 'tabs' }} onReady={() => setReady(true)} />

      {errorMsg && (
        <p style={{ margin: 0, fontSize: 12, color: V4.danger, lineHeight: 1.4 }} role="alert">
          {errorMsg}
        </p>
      )}

      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
        {onCancel && (
          <Button variant="secondary" onClick={onCancel} disabled={submitting}>
            Cancelar
          </Button>
        )}
        <Button
          type="submit"
          variant="primary"
          loading={submitting}
          disabled={!stripe || !elements || !ready || submitting}
        >
          Pagar y activar
        </Button>
      </div>
    </form>
  );
};

// PRD-014 FR-28 — Formulario de pago con tarjeta (Stripe Elements).
// Recibe el client_secret de la suscripción incomplete y monta PaymentElement.
// Maneja 3DS/SCA vía confirmPayment. Si la publishable key no está configurada,
// muestra un fallback (no rompe el flujo de transferencia).
export const StripePaymentForm = ({ clientSecret, onSuccess, onCancel }: StripePaymentFormProps) => {
  const promise = useMemo(() => getStripePromise(), []);

  if (!promise) {
    return (
      <div
        style={{
          padding: 16,
          borderRadius: 10,
          border: `1px solid ${V4.grayLight}`,
          background: V4.grayUltraLight,
          fontSize: 13,
          color: V4.primary,
          lineHeight: 1.5,
        }}
      >
        El pago con tarjeta no está disponible en este momento. Por favor selecciona transferencia bancaria o contacta a
        soporte.
      </div>
    );
  }

  return (
    <Elements
      stripe={promise}
      options={{
        clientSecret,
        appearance: {
          theme: 'stripe',
          variables: {
            colorPrimary: V4.secondary,
            colorText: V4.primary,
            colorDanger: V4.danger,
            fontFamily: 'Poppins, sans-serif',
            borderRadius: '8px',
          },
        },
      }}
    >
      <InnerForm onSuccess={onSuccess} onCancel={onCancel} />
    </Elements>
  );
};
